import { useState } from "react";
import { X, LayoutList, LayoutGrid } from "lucide-react";
import QuestionInput from "./QuestionInput";
import CardLayout from "./CardLayout";
import type { LayoutType } from "../../store/useQuestionnaireStore";

interface PreviewProps {
  formData: {
    name: string;
    description: string;
    questions: Array<{
      question: string;
      answerType: string;
      options: string[];
      required: boolean;
      placeholder: string;
    }>;
  };
  layout?: LayoutType;
  onClose: () => void;
}

const QuestionnairePreview = ({ formData, layout = "form", onClose }: PreviewProps) => {
  // BDs can preview EITHER layout regardless of which one is selected, so they
  // can compare before sharing. Defaults to the currently-selected layout.
  const [view, setView] = useState<LayoutType>(layout);

  // CardLayout / QuestionInput need an `_id` per question; synthesize one.
  const previewQuestions = formData.questions.map((q, idx) => ({
    _id: `preview-${idx}`,
    question: q.question,
    answerType: q.answerType as any,
    options: q.options,
    required: q.required,
    placeholder: q.placeholder,
  }));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between gap-4 z-10">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-800">Preview Mode</h2>
            <p className="text-sm text-gray-500 mt-1">This is how clients will see the questionnaire</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* Toggle between the two layouts so BDs can compare before sharing */}
            <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
              <button
                onClick={() => setView("form")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  view === "form" ? "bg-white shadow text-[#2462b2]" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <LayoutList size={16} /> Form
              </button>
              <button
                onClick={() => setView("card")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  view === "card" ? "bg-white shadow text-[#2462b2]" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <LayoutGrid size={16} /> Card
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-3 rounded-full hover:bg-gray-100 transition-all duration-200"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>
        </div>

        {view === "card" ? (
          <div className="p-6 sm:p-8 bg-gray-50">
            {formData.questions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 font-medium">No questions added yet</p>
                <p className="text-sm text-gray-400 mt-1">Add questions to see the card preview</p>
              </div>
            ) : (
              <CardLayout
                name={formData.name || "Untitled Questionnaire"}
                description={formData.description}
                questions={previewQuestions}
                answers={{}}
                onChange={() => {}}
                preview
              />
            )}
          </div>
        ) : (
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{formData.name || "Untitled Questionnaire"}</h1>
            <p className="text-gray-600">{formData.description || "No description provided"}</p>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-medium">Preview Note: This is a read-only preview. Actual form will be interactive.</p>
          </div>

          <div className="space-y-6">
            {formData.questions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 font-medium">No questions added yet</p>
                <p className="text-sm text-gray-400 mt-1">Add questions to see preview</p>
              </div>
            ) : (
              formData.questions.map((q, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <label className="font-semibold text-gray-900 text-lg">
                      {idx + 1}. {q.question || "Untitled Question"}
                      {q.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                      {q.answerType}
                    </span>
                  </div>
                  <QuestionInput
                    question={{
                      _id: `preview-${idx}`,
                      question: q.question,
                      answerType: q.answerType as any,
                      options: q.options,
                      required: q.required,
                      placeholder: q.placeholder,
                    }}
                    value={q.answerType === "checkbox" ? [] : ""}
                    onChange={() => {}}
                    disabled={true}
                  />
                  {q.placeholder && (
                    <p className="text-xs text-gray-500 mt-2 italic">Placeholder: {q.placeholder}</p>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <button
              disabled
              className="w-full bg-gray-300 text-gray-500 font-bold py-3 rounded-xl cursor-not-allowed"
            >
              Submit (Preview Only)
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default QuestionnairePreview;
